import { EligibilityInfo } from "../types/eligibility";

interface EligibilityCheckProps {
  patientId: string;
  onPatientIdChange: (value: string) => void;
  eligibilityInfo: EligibilityInfo;
}

const EligibilityCheck = ({
  patientId,
  onPatientIdChange,
  eligibilityInfo,
}: EligibilityCheckProps) => {
  return (
    <div>
      {/* Search Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Patient Eligibility</h2>
      </div>
      <div className="flex gap-4 mb-8">
        <input
          type="text"
          value={patientId}
          onChange={(e) => onPatientIdChange(e.target.value)}
          placeholder="Enter Patient ID"
          className="flex-1 p-2 border rounded"
        />
        <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Check Eligibility
        </button>
      </div>

      {/* Results Section */}
      {patientId && eligibilityInfo && (
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4">Coverage Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium">Status</label>
                <p className="text-green-600 font-medium">
                  {eligibilityInfo.isEligible ? "Active" : "Inactive"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium">Plan Type</label>
                <p>{eligibilityInfo.plan}</p>
              </div>
              <div>
                <label className="block text-sm font-medium">Network</label>
                <p>{eligibilityInfo.network}</p>
              </div>
              <div>
                <label className="block text-sm font-medium">Group</label>
                <p>{eligibilityInfo.group}</p>
              </div>
              <div>
                <label className="block text-sm font-medium">Deductible</label>
                <p>{eligibilityInfo.deductible}</p>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Remaining Benefit
                </label>
                <p>{eligibilityInfo.remainingBenefit}</p>
              </div>
            </div>
          </div>

          {/* Procedure Codes */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4">Procedure Coverage</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="p-2 text-left font-medium">Category</th>
                    <th className="p-2 text-left font-medium">Code</th>
                    <th className="p-2 text-left font-medium">Description</th>
                    <th className="p-2 text-left font-medium">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibilityInfo.codes.map((procedure, index, arr) => {
                    // Get category from first word of description
                    const category = procedure.desc.split(" ")[0];
                    // Check if this is the first procedure in its category
                    const isFirstInCategory =
                      index === 0 ||
                      arr[index - 1].desc.split(" ")[0] !== category;

                    return (
                      <tr
                        key={procedure.code}
                        className="border-b hover:bg-gray-50"
                      >
                        {/* Only show category on first row of its type */}
                        {isFirstInCategory ? (
                          <td className="p-2 font-medium">{category}</td>
                        ) : (
                          <td className="p-2"></td>
                        )}
                        <td className="p-2 font-mono">{procedure.code}</td>
                        <td className="p-2">{procedure.desc}</td>
                        <td
                          className={`p-2 font-medium ${
                            procedure.cov === "100%"
                              ? "text-green-600"
                              : procedure.cov === "80%"
                              ? "text-blue-600"
                              : "text-orange-600"
                          }`}
                        >
                          {procedure.cov}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-2">Coverage Notes</h3>
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              <li>Coverage percentages shown are for in-network providers</li>
              <li>Annual maximum applies to all services</li>
              <li>Preventive services do not count toward annual maximum</li>
              <li>Pre-authorization required for major services over $500</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default EligibilityCheck;
